import {SyntheticEvent, useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {Box, Divider, Stack, Typography, Tab, Tabs} from "@mui/material";
import TabContent from "@components/TabContent";
import ServerSideDataTable from "@components/ServerSideDataTable";
import {useNavigate, useParams} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import {ArrowBack} from "@mui/icons-material";
import {urls} from "@routes";
import { formatInTimeZone } from "date-fns-tz";

interface PageData {
	status_code: string
	status_text: string

	emails: EmailID[]
}

interface EmailID {
	address: string
	name: string
	subdomain: string
}

interface BlacklistPageData {
	domain: string
	blacklists: BlacklistData[]
	time_zone: string
}

interface BlacklistData {
	blacklist_name: string
	blacklist_url: string
	status: string
	risk: string
	last_run_on: string 
	next_run_on: string
}

export default function Emails() {
	const {domain} = useParams();
	const navigate = useNavigate();

	const [pageData, setPageData] = useState<PageData>();
	const [tabValue, setTabValue] = useState<number>(0);

	// Query to fetch all users.
	const fetchAllEmails = useQuery({
		queryKey: ["fetchAllEmails"],
		queryFn: () => authenticateAndFetchData(`/email-accounts/emails/?domain=${domain || ""}`),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});
	useEffect(() => {
		if (fetchAllEmails.data) {
			setPageData(fetchAllEmails.data.data as PageData);
		}
	}, [fetchAllEmails.data]);

	// Columns for user table.
	const columnDefs: ColDef[] = [
		{field: "address", headerName: "Email ID"},
		{field: "name", headerName: "Sender Name"},
		{field: "subdomain", headerName: "Email Subdomain"},
	]

	const handleTabChange = async (event: SyntheticEvent, newValue: number) => {            
		setTabValue(newValue);
	};
	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (fetchAllEmails.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (fetchAllEmails.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={fetchAllEmails.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={4} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<IconButton onClick={() => {
						navigate(urls["emailAccounts"])
					}}>
						<ArrowBack/>
					</IconButton>
					<Box sx={{borderBottom: 1, borderColor: 'divider', mt: 1}}>            
						<Tabs value={tabValue} onChange={handleTabChange} sx={{
							'& .MuiTabs-flexContainer': {
								flexWrap: 'wrap',
							},
						}}>
							<Tab label={`All Emails Under ${domain || ""}`} />
							<Tab label="Blacklist" />							
						</Tabs>
					</Box>
					{/* <Typography fontWeight={"bold"} variant={"h5"}>All Emails Under {domain || ""}</Typography> */}
				</Stack>

				<Divider/>
				<TabContent value={tabValue} index={0}>
					<Box sx={{height: "680px"}}>					
						<ServerSideDataTable columns={columnDefs} rows={pageData.emails}/>					
					</Box>
				</TabContent>
				<TabContent value={tabValue} index={1}>
					<BlacklistCheck 
						domain={domain ?? ""}
					/>
				</TabContent>
			</Stack>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}


function BlacklistCheck({ domain }: { domain: string }) {
	const [blacklistData, setBlacklistData] = useState<BlacklistPageData>()
	const blacklistDomains = useQuery({
			queryKey: ["blacklistDomains"],
			queryFn: () => authenticateAndFetchData(`/email-accounts/blacklist-data/?domain=${domain || ""}`),
			gcTime: 0,
			refetchOnWindowFocus: false,
			retry: retryFn,
		});
		useEffect(() => {
			if (blacklistDomains.data) {
				setBlacklistData(blacklistDomains.data.data as BlacklistPageData);
			}
		}, [blacklistDomains.data]);

	function timestampFormatter(params: ValueFormatterParams, timezone: string) {
			if (!params.value) return "";
	
			return formatInTimeZone(
				new Date(params.value),
				timezone,
				"hh:mm a, do MMM yyyy"
			).replace(/\b(AM|PM)\b/, (match) => match.toLowerCase());
		}
	
	const columns = [
		{ field: "blacklist_name", headerName: "Blacklist Name", flex: 1 },
		{ field: "blacklist_url", headerName: "Blacklist Url", flex: 1 },
		{ field: "status", headerName: "Status", flex: 1 },
		{ field: "risk", headerName: "Risk", flex: 1 },
		{ field: "last_run_on", headerName: "Last Run on", valueFormatter: (params) => timestampFormatter(params, blacklistData?.time_zone ?? "UTC"), flex: 1 },
		{ field: "next_run_on", headerName: "Next Run on", valueFormatter: (params) => timestampFormatter(params, blacklistData?.time_zone ?? "UTC"), flex: 1 },
	];

	if (blacklistDomains.isLoading) {
		return <PageLoading/>;
	} else if (blacklistDomains.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={blacklistDomains.error as unknown as ApiRequestFailed}/>;
	} else if (blacklistData) {
		return (
			<Box sx={{height: "680px"}}>
				<ServerSideDataTable columns={columns} rows={blacklistData.blacklists} />
			</Box>
		);
	}
	
	return <></>;
}
